import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import Dashboard from './components/Dashboard';
import PowerManagement from './components/PowerManagement';
import CameraManagement from './components/CameraManagement';
import config from './config';

// ログインボタンコンポーネント
const SignInButton = () => {
  const { instance } = useMsal();

  // ログイン処理を行う関数
  const handleLogin = () => {
    // リダイレクトでログイン画面を表示
    instance.loginRedirect(loginRequest)
      .catch(error => {
        console.error('ログイン中にエラーが発生しました:', error);
      });
  };

  return (
    <button onClick={handleLogin} className="button button-primary">
      ログイン
    </button>
  );
};

// ログアウトボタンコンポーネント
const SignOutButton = () => {
  const { instance } = useMsal();

  // ログアウト処理を行う関数
  const handleLogout = () => {
    // ログアウト処理を実行
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    })
      .catch(error => {
        console.error('ログアウト中にエラーが発生しました:', error);
      });
  };

  return (
    <button onClick={handleLogout} className="button button-secondary">
      ログアウト
    </button>
  );
};

// リンクボタンコンポーネント (メニュー項目用)
const NavLinkButton = ({ url, title, icon, onClick }) => {
  const { instance, accounts } = useMsal();

  const handleClick = async (e) => {
    // カスタムクリックハンドラがある場合はそれを実行
    if (onClick) {
      e.preventDefault(); // デフォルトのリンク遷移を防止
      onClick();
      return;
    }
    
    // 外部URLへの遷移の場合は新しいタブで開く
    if (url && url.startsWith('http')) {
      e.preventDefault(); // デフォルトのリンク遷移を防止
      
      try {
        // 現在のアカウントを取得
        const currentAccount = accounts[0];
        
        if (!currentAccount) {
          console.error('アカウント情報が見つかりません');
          window.open(url, '_blank');
          return;
        }
        
        console.log('アカウント情報:', currentAccount);
        
        // アクセストークンを取得（インタラクティブな方法を試みる）
        let tokenResponse;
        try {
          // まずサイレントでトークン取得を試みる
          tokenResponse = await instance.acquireTokenSilent({
            scopes: loginRequest.scopes,
            account: currentAccount
          });
          console.log('サイレントトークン取得成功');
        } catch (silentError) {
          console.warn('サイレントトークン取得に失敗、インタラクティブな方法を試みます:', silentError);
          // サイレント取得に失敗した場合、ポップアップでトークン取得を試みる
          tokenResponse = await instance.acquireTokenPopup({
            scopes: loginRequest.scopes,
            account: currentAccount
          });
          console.log('ポップアップトークン取得成功');
        }
        
        console.log('トークンレスポンス:', tokenResponse);
        
        if (tokenResponse && tokenResponse.accessToken) {
          console.log('アクセストークン取得成功:', tokenResponse.accessToken.substring(0, 10) + '...');
          
          // トークンをセッションストレージに保存
          // 注意: これは同一オリジンポリシーにより、別ドメインからはアクセスできません
          sessionStorage.setItem('msal.accessToken', tokenResponse.accessToken);
          
          // IDトークンがある場合は保存
          if (tokenResponse.idToken) {
            sessionStorage.setItem('msal.idToken', tokenResponse.idToken);
          }
          
          // 新しいウィンドウでリンクを開く
          const newWindow = window.open('about:blank', '_blank');
          
          // HTML形式でトークン情報を渡す
          if (newWindow) {
            newWindow.document.write(`
              <!DOCTYPE html>
              <html>
              <head>
                <title>リダイレクト中...</title>
                <script>
                  // トークンをローカルストレージに保存
                  sessionStorage.setItem('msal.accessToken', "${tokenResponse.accessToken}");
                  ${tokenResponse.idToken ? `sessionStorage.setItem('msal.idToken', "${tokenResponse.idToken}");` : ''}
                  
                  // リダイレクト先URLに移動
                  window.location.href = "${url}";
                </script>
              </head>
              <body>
                <p>リダイレクト中です。しばらくお待ちください...</p>
              </body>
              </html>
            `);
          } else {
            // ポップアップがブロックされた場合は通常のURLを開く
            console.warn('ポップアップがブロックされました。トークン付きURLを直接開きます。');
            window.open(`${url}${url.includes('?') ? '&' : '?'}token=${tokenResponse.accessToken}`, '_blank');
          }
        } else {
          console.error('トークンが取得できませんでした');
          window.open(url, '_blank');
        }
      } catch (error) {
        console.error('トークン取得エラー:', error);
        // エラーが発生した場合は、トークンなしでリンクを開く
        window.open(url, '_blank');
      }
    }
    // 内部リンクの場合はデフォルトの遷移を許可
  };

  return (
    <a href={url} onClick={handleClick} className="nav-link">
      {icon && <span className="menu-icon">{icon}</span>}
      <span className="menu-title">{title}</span>
    </a>
  );
};


// メニューアイコン定義
const MenuIcons = {
  RECORD: '📝',
  FIELD: '🏗️',
  DRIVE: '🚗',
  DASHBOARD: '📊',
  SNOW: '❄️',
  POWER: '⚡',
  CAMERA: '📹',
  SHIPMENT: '📦', // 出荷管理用アイコン追加
  GUARD: '🛡️', // ガードナビデータ表示用アイコン
  BATTERY: '🔋', // バッテリーチェック用アイコン
  IMAGE: '🖼️'  // 画像解析用アイコン
};

// メインアプリケーションコンポーネント
function App() {
  const { instance, accounts, inProgress } = useMsal();
  const [showPowerManagement, setShowPowerManagement] = useState(false);
  const [showCameraManagement, setShowCameraManagement] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true); // ダッシュボード表示フラグ
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); // ドロワーメニュー開閉状態

  // 認証状態と外部サービスからの遷移をチェックするuseEffect
  useEffect(() => {
    console.log('認証状態チェック - アカウント:', accounts.length, '認証処理状態:', inProgress);
    
    // 認証処理が完了しており、アカウント情報が存在する場合（認証済みの場合）
    if (accounts.length > 0 && inProgress === 'none') {
      console.log('認証済みユーザー:', accounts[0].name);
      setIsAuthenticated(true);
      
      // URLパラメータとリファラーを確認
      const urlParams = new URLSearchParams(window.location.search);
      const referrer = document.referrer || '';
      console.log('URLパラメータ:', urlParams.toString(), 'リファラー:', referrer);
      
      // 外部サービスからの遷移かどうかを判断（条件を拡張）
      const fromExternalService = 
        urlParams.has('source') || 
        urlParams.has('from') || 
        urlParams.has('external') ||
        referrer.includes('frontdoor.azure.com') ||
        referrer.includes('azure-api.net') ||
        referrer.includes('azurewebsites.net') ||
        window.location.href.includes('?auth=') ||
        sessionStorage.getItem('fromExternal') === 'true';
      
      // デバッグ情報
      console.log('外部サービスからの遷移:', fromExternalService);
      
      // 認証済みかつ外部サービスからの遷移の場合、ダッシュボードを非表示にする
      if (isAuthenticated && fromExternalService) {
        console.log('認証済みかつ外部サービスからの遷移を検出、ダッシュボードを非表示');
        setShowDashboard(false);
      } else {
        setShowDashboard(true);
      }
    } else if (accounts.length === 0 && inProgress === 'none') {
      setIsAuthenticated(false);
      setShowDashboard(true); // 未認証の場合はダッシュボード（ログイン画面）を表示
    }
  }, [accounts, inProgress, isAuthenticated]);

  // メニュー項目の定義
  const menuItems = [
    { id: 'record', title: '１．記録CH', url: config.urls.recordCH, icon: MenuIcons.RECORD },
    { id: 'field', title: '２．現場WEB', url: config.urls.fieldWeb, icon: MenuIcons.FIELD },
    { id: 'vehicle', title: '３．運転記録', url: config.urls.vehicleRecord, icon: MenuIcons.DRIVE },
    { id: 'snow', title: '４．融雪剤管理', url: config.urls.snowMelter, icon: MenuIcons.SNOW },
    { 
      id: 'power', 
      title: '５．電源管理', 
      url: '#', 
      icon: MenuIcons.POWER,
      onClick: () => {
        setShowPowerManagement(true);
        setShowCameraManagement(false);
        setIsDrawerOpen(false); // ドロワーを閉じる
      }
    },
    { 
      id: 'camera', 
      title: '６．カメラ', 
      url: '#', 
      icon: MenuIcons.CAMERA,
      onClick: () => {
        setShowCameraManagement(true);
        setShowPowerManagement(false);
        setIsDrawerOpen(false); // ドロワーを閉じる
      }
    },
    { id: 'shipment', title: '７．出荷管理', url: config.urls.shipmentManager, icon: MenuIcons.SHIPMENT },
    { id: 'guardnavi', title: '８．ガードナビデータ表示', url: config.urls.guardNavi, icon: MenuIcons.GUARD },
    { id: 'battery', title: '９．バッテリーチェック', url: config.urls.batteryCheck, icon: MenuIcons.BATTERY },
    { id: 'image', title: '１０．画像解析', url: config.urls.imageAnalysis, icon: MenuIcons.IMAGE }
  ];

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  return (
    <div className="App">
      {/* 認証状態に応じて表示するコンテンツを切り替え */}
      {isAuthenticated ? (
        // 認証済みユーザー向けのコンテンツ
        <div>
          {/* ヘッダー */}
          <header className="header">
            <div className="header-container">
              <div className="logo">
                <h1>仙台銘板プラットフォーム</h1>
              </div>
              <div className="utility-links">
                {accounts.length > 0 && (
                  <span className="username">{accounts[0]?.name || 'ユーザー'}</span>
                )}
                <SignOutButton />
              </div>
              <button 
                className="hamburger-button" 
                aria-expanded={isDrawerOpen} 
                aria-controls="drawer-menu" 
                aria-label={isDrawerOpen ? "メニューを閉じる" : "メニューを開く"}
                onClick={toggleDrawer}
              >
                <span className="hamburger-icon"></span>
              </button>
            </div>
            
            {/* グローバルナビゲーション - デスクトップ */}
            <nav className="global-nav" aria-label="グローバルナビゲーション">
              <ul className="nav-list">
                {menuItems.map(item => (
                  <li key={item.id} className="nav-item">
                    <NavLinkButton 
                      url={item.url} 
                      title={item.title} 
                      icon={item.icon} 
                      onClick={item.onClick} 
                    />
                  </li>
                ))}
              </ul>
            </nav>
            
            {/* ドロワーメニュー - モバイル */}
            <div 
              id="drawer-menu" 
              className={`drawer ${isDrawerOpen ? 'drawer-open' : ''}`} 
              aria-hidden={!isDrawerOpen}
            >
              <nav className="drawer-nav" aria-label="メニュー">
                <ul className="drawer-nav-list">
                  {menuItems.map(item => (
                    <li key={item.id} className="drawer-nav-item">
                      <NavLinkButton 
                        url={item.url} 
                        title={item.title} 
                        icon={item.icon} 
                        onClick={item.onClick} 
                      />
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </header>

          {/* メインコンテンツ */}
          <main className="main">
            <div className="container">
              {/* 電源管理またはカメラ管理が表示されている場合 */}
              {showPowerManagement ? (
                <div className="dashboard-wrapper">
                  <h3>電源管理</h3>
                  <PowerManagement />
                  <button onClick={() => setShowPowerManagement(false)} className="button button-secondary mt-4">
                    閉じる
                  </button>
                </div>
              ) : showCameraManagement ? (
                <div className="dashboard-wrapper">
                  <h3>クラウドカメラ管理</h3>
                  <CameraManagement />
                  <button onClick={() => setShowCameraManagement(false)} className="button button-secondary mt-4">
                    閉じる
                  </button>
                </div>
              ) : (
                /* ダッシュボード表示フラグに応じてダッシュボードを表示するかどうかを決定 */
                showDashboard ? (
                  <div className="dashboard-section">
                    <Dashboard />
                  </div>
                ) : (
                  // ダッシュボード非表示時はメニューのみ表示（または何も表示しない）
                  null 
                )
              )}
            </div>
          </main>
        </div>
      ) : (
        // 未認証の場合に表示するコンテンツ (ログイン画面)
        <div className="login-container">
          <h1>仙台銘板プラットフォーム</h1>
          <p>ログインして、アプリケーションにアクセスしてください。</p>
          <SignInButton />
        </div>
      )}
    </div>
  );
}

export default App;
